import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Dialog } from '@/components/ui/organisms/Dialog';

import {
  JobFlowDialogChrome,
  JobFlowModalFooterActions,
  jobFlowDialogBodyClassName,
  jobFlowDialogClassName,
  jobFlowDialogContentClassName,
  jobFlowDialogFooterClassName,
} from '../create-job-modal/jobFlowModalChrome';
import {
  JobRefineSectionFields,
  JobRefineSectionHeader,
  REFINE_SECTIONS,
  REVIEW_SECTION,
  RefineSectionKey,
} from '../create-job-modal/jobRefineSections';
import { useJobEditImpact, useUpdateJobOffer } from '../../../hooks/useJobOffers';
import { JobOffer, MatchingPriorities, Skill } from '../../../models/job';
import { JobEditStrategy, JobRescoreSummary } from '../../../types/job.types';
import { EditJobReviewPhase } from './EditJobReviewPhase';
import {
  buildUpdatePayload,
  EditFormData,
  formFromJob,
  saveToastMessage,
} from './editJobPayload';

interface EditJobDetailsModalProps {
  job: JobOffer;
  onClose: () => void;
}

type EditPhase = 'details' | 'review';

const LAST_REFINE_IDX = REFINE_SECTIONS.length - 1;

/** Edit job — same shell + section UI as create/refine. Edit → Validate. */
export const EditJobDetailsModal: React.FC<EditJobDetailsModalProps> = ({ job, onClose }) => {
  const { t } = useTranslation('job');
  const { t: tDashboard } = useTranslation('dashboard');

  const initialForm = useMemo(() => formFromJob(job), [job.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const [form, setForm] = useState<EditFormData>(initialForm);
  const [phase, setPhase] = useState<EditPhase>('details');
  const [refineIdx, setRefineIdx] = useState(0);
  const [impact, setImpact] = useState<JobRescoreSummary | null>(null);
  const [editStrategy, setEditStrategy] = useState<JobEditStrategy>('update_job');

  const updateJob = useUpdateJobOffer(job.id);
  const editImpact = useJobEditImpact(job.id);

  const payload = useMemo(() => buildUpdatePayload(initialForm, form), [initialForm, form]);
  const hasChanges = Object.keys(payload).length > 0;
  const isSaving = updateJob.isPending;
  const isReview = phase === 'review';
  const refineSection = REFINE_SECTIONS[refineIdx];
  const showStrategyCards = Boolean(impact?.scoring_changed);

  const updateField = useCallback(
    (fieldName: string, value: string | Skill[] | MatchingPriorities) => {
      setForm((prev) => ({ ...prev, [fieldName]: value }));
    },
    []
  );

  useEffect(() => {
    if (!isReview) return;
    setImpact(null);
    setEditStrategy('update_job');
    if (!hasChanges) return;
    editImpact.mutate(payload, {
      onSuccess: setImpact,
      onError: () => setImpact(null),
    });
    // editImpact identity changes each render; mutate is what we need on enter Validate.
  }, [isReview, hasChanges, payload]); // eslint-disable-line react-hooks/exhaustive-deps

  const goNext = () => {
    if (phase !== 'details') return;
    if (refineSection.key === 'details' && !String(form['Job Title'] ?? '').trim()) {
      toast.error(t('edit_job_modal.title_required'));
      return;
    }
    if (refineIdx < LAST_REFINE_IDX) {
      setRefineIdx((i) => i + 1);
      return;
    }
    setPhase('review');
  };

  const goBack = () => {
    if (phase === 'review') {
      setPhase('details');
      setRefineIdx(LAST_REFINE_IDX);
      return;
    }
    setRefineIdx((i) => Math.max(i - 1, 0));
  };

  const handleSave = () => {
    if (!hasChanges) {
      onClose();
      return;
    }
    updateJob.mutate(
      {
        ...payload,
        edit_strategy: showStrategyCards ? editStrategy : 'update_job',
      },
      {
        onSuccess: ({ rescore }) => {
          toast.success(saveToastMessage(t, rescore));
          onClose();
        },
        onError: (error: Error) => {
          toast.error(t('edit_job_modal.save_failed', { error: error.message }));
        },
      }
    );
  };

  const headerTitle = t('edit_job_modal.header');
  const primaryLabel = isReview
    ? !hasChanges
      ? t('edit_job_modal.close')
      : showStrategyCards && editStrategy === 'update_job_exam_and_rescore'
        ? t('edit_job_modal.save_and_rescore')
        : t('edit_job_modal.save')
    : tDashboard('create_job_modal.continue');

  return (
    <Dialog
      open
      onOpenChange={(nextOpen) => {
        if (!nextOpen && !isSaving) onClose();
      }}
      title={headerTitle}
      maxWidth="2xl"
      backdropZIndex={100}
      closeOnBackdropClick={!isSaving}
      className={jobFlowDialogClassName}
      contentClassName={jobFlowDialogContentClassName}
    >
      <JobFlowDialogChrome
        title={headerTitle}
        closeAriaLabel={t('edit_job_modal.close')}
        showCloseButton
        closeDisabled={isSaving}
        stepperSteps={[
          { key: 'details', label: t('edit_job_modal.phase_details') },
          { key: 'review', label: t('edit_job_modal.phase_review') },
        ]}
        stepperCurrent={phase}
      />

      <Dialog.Body className={jobFlowDialogBodyClassName}>
        <div className="shrink-0">
          <JobRefineSectionHeader
            sections={isReview ? [REVIEW_SECTION] : REFINE_SECTIONS}
            sectionIdx={isReview ? 0 : refineIdx}
            progress={isReview ? 'none' : 'pills'}
          />
        </div>

        <div className="flex min-h-0 flex-1 flex-col">
          {isReview ? (
            <EditJobReviewPhase
              hasChanges={hasChanges}
              isImpactPending={editImpact.isPending}
              isImpactError={editImpact.isError}
              impact={impact}
              editStrategy={editStrategy}
              onStrategyChange={setEditStrategy}
              isSaving={isSaving}
            />
          ) : (
            <JobRefineSectionFields
              sectionKey={refineSection.key as RefineSectionKey}
              getValue={(fieldName) => form[fieldName]}
              onChange={updateField}
              defaultMatchingPriorities={job.matchingPriorities}
            />
          )}
        </div>
      </Dialog.Body>

      <Dialog.Footer className={jobFlowDialogFooterClassName}>
        <JobFlowModalFooterActions
          backLabel={tDashboard('create_job_modal.back')}
          primaryLabel={primaryLabel}
          onBack={goBack}
          onPrimary={isReview ? handleSave : goNext}
          backDisabled={!(isReview || refineIdx > 0) || isSaving}
          primaryDisabled={isSaving || (isReview && hasChanges && editImpact.isPending)}
        />
      </Dialog.Footer>
    </Dialog>
  );
};
